import moment from 'moment-timezone';

const langFormatMapping: any = {
  en: 'MMM D YYYY h:mm A',
  fr: 'D MMM YYYY HH:mm',
  de: 'D. MMM. YYYY HH:mm',
  default: 'MMM D YYYY h:mm A',
};

export const formatDateToLocale = (locale: any, date: string) => {
  if (!date || date === '') {
    return null;
  }

  const dateSplit: string[] = date.split('.');
  const datetime: string[] = dateSplit[0].replace('T', ' ').split(' ');
  const dateParts: string[] = datetime[0].split('-');
  const timeParts: string[] = datetime[1].split(':');

  const dateFormatted = new Date(
    parseInt(dateParts[0], 10),
    parseInt(dateParts[1], 10) - 1,
    parseInt(dateParts[2], 10),
    parseInt(timeParts[0], 10),
    parseInt(timeParts[1], 10),
    parseInt(timeParts[2], 10),
  );

  const result = Number.isNaN(dateFormatted.getTime()) ? '' : dateFormatted.toLocaleString(locale);

  return result;
};

export const formatMysqlDateToLocale = (locale: any, date: string) => {
  if (!date || date === '') {
    return null;
  }

  const jsDate = new Date(Date.parse(date.replace(/-/g, '/')));
  return `${jsDate.toLocaleString(locale)}`;
};

export const getNowFormattedByTimezone = () => {
  const userLang = navigator.language;
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateFormat = langFormatMapping[userLang] || langFormatMapping.default;
  const now = moment().toDate();
  return moment.tz(now, userTimezone).format(dateFormat);
};

export const formatDate = (date: string, startFormat: string, endFormat: string, useUserTimezone = true) => {
  const dateMoment = moment(date, startFormat);
  let returnValue = null;

  if (useUserTimezone) {
    const userLang = navigator.language;
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    returnValue = moment.tz(dateMoment.toDate(), userTimezone).format(endFormat);
  } else {
    returnValue = dateMoment.format(endFormat);
  }

  return returnValue;
};

// Helper function to check if date is within filter range
export const isDateInRange = (templateDate: string, filterValue: any): boolean => {
  if (!templateDate || !filterValue) return true;

  try {
    // Parse template date from backend format "2025-07-21 11:15:26"
    const templateMoment = moment(templateDate);
    if (!templateMoment.isValid()) return true;

    // Handle different filter formats from CustomDatePicker
    if (typeof filterValue === 'object') {
      // Handle interval object with start and end dates
      if (filterValue.interval) {
        const interval = filterValue.interval;
        if (interval === 'customDateRange' && filterValue.customDateRange) {
          const [startDate, endDate] = filterValue.customDateRange;
          if (startDate && endDate) {
            const start = moment(startDate).startOf('day');
            const end = moment(endDate).endOf('day');
            return templateMoment.isBetween(start, end, null, '[]');
          }
        }
        // Handle predefined intervals using moment
        const now = moment();
        
        switch (interval) {
          case 'lastDay':
            const last24Hours = now.clone().subtract(1, 'day');
            return templateMoment.isBetween(last24Hours, now, null, '[]');
          case 'last3Days':
            const last3Days = now.clone().subtract(3, 'days').startOf('day');
            return templateMoment.isBetween(last3Days, now, null, '[]');
          case 'last7Days':
            const last7Days = now.clone().subtract(7, 'days').startOf('day');
            return templateMoment.isBetween(last7Days, now, null, '[]');
          case 'last14Days':
            const last14Days = now.clone().subtract(14, 'days').startOf('day');
            return templateMoment.isBetween(last14Days, now, null, '[]');
          case 'last30Days':
            const last30Days = now.clone().subtract(30, 'days').startOf('day');
            return templateMoment.isBetween(last30Days, now, null, '[]');
          case 'last3Months':
            const last3Months = now.clone().subtract(3, 'months').startOf('day');
            return templateMoment.isBetween(last3Months, now, null, '[]');
          case 'last12Months':
            const last12Months = now.clone().subtract(12, 'months').startOf('day');
            return templateMoment.isBetween(last12Months, now, null, '[]');
          default:
            return true;
        }
      }
      
      // Handle array of dates (range)
      if (Array.isArray(filterValue) && filterValue.length === 2) {
        const [startDate, endDate] = filterValue;
        if (startDate && endDate) {
          const start = moment(startDate).startOf('day');
          const end = moment(endDate).endOf('day');
          return templateMoment.isBetween(start, end, null, '[]');
        }
      }
      
      // Handle single date object
      if (filterValue instanceof Date) {
        const filterMoment = moment(filterValue);
        return templateMoment.isSame(filterMoment, 'day');
      }
    }
    
    // Handle string date
    if (typeof filterValue === 'string') {
      const filterMoment = moment(filterValue);
      if (filterMoment.isValid()) {
        return templateMoment.isSame(filterMoment, 'day');
      }
    }
  } catch (error) {
    console.warn('Date filtering error:', error);
    return true;
  }
  
  return true;
};
